package com.schoolconnect.app.feature.parentnotes

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.clickable
import androidx.compose.foundation.border
import androidx.compose.foundation.background
import androidx.compose.foundation.Image
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppButton
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogLabeledTextField
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSectionLabel
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppSearchableDropdownField
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.util.decodeImageBitmap
import com.schoolconnect.app.util.resizeImage
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerMode
import io.github.vinceglb.filekit.core.PickerType
import io.github.vinceglb.filekit.core.PlatformFile
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.cloud_upload
import ischoolconnect.sharedui.generated.resources.document
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.message
import ischoolconnect.sharedui.generated.resources.phone
import ischoolconnect.sharedui.generated.resources.user
import ischoolconnect.sharedui.generated.resources.users
import org.jetbrains.compose.resources.painterResource
import kotlinx.coroutines.launch

private data class ParentNoteAttachmentUi(
    val file: PlatformFile?,
    val name: String,
    val type: String,
    val sizeLabel: String,
    val fileId: String? = null,
    val previewBytes: ByteArray? = null
)

internal data class ParentNoteStudentPrefill(
    val studentName: String,
    val className: String,
    val section: String,
    val studentId: String = "",
    val rollNumber: String = ""
)

@Composable
internal fun AddParentNoteDialog(
    visible: Boolean,
    onDismiss: () -> Unit,
    initialNote: ParentNoteRowUi? = null,
    initialStudent: ParentNoteStudentPrefill? = null,
    pickerState: ParentNotesUiState = ParentNotesUiState(),
    onClassSectionSelected: (String?) -> Unit = {},
    onStudentPickerSearchChanged: (String) -> Unit = {},
    onStudentPickerLoadMore: () -> Unit = {},
    onStudentSelected: (String) -> Unit = {},
    onSave: (ParentNoteDraft) -> Unit = {},
    onUpdate: (ParentNoteEditDraft) -> Unit = {},
    errorMessage: String = ""
) {
    if (!visible) return

    var selectedClass by remember(initialNote, initialStudent) { mutableStateOf(initialNote?.className ?: initialStudent?.className ?: "") }
    var selectedSection by remember(initialNote, initialStudent) { mutableStateOf(initialNote?.section ?: initialStudent?.section ?: "") }
    var selectedStudent by remember(initialNote, initialStudent) { mutableStateOf(initialNote?.studentName ?: initialStudent?.studentName ?: "") }
    var title by remember(initialNote) { mutableStateOf(initialNote?.title ?: "") }
    var message by remember(initialNote) { mutableStateOf(initialNote?.detail.orEmpty()) }
    var validationError by remember { mutableStateOf("") }
    var hasSubmitted by remember { mutableStateOf(false) }
    val classError = if (hasSubmitted && selectedClass.isBlank()) "Class is required." else ""
    val titleError = if (hasSubmitted && title.isBlank()) "Note title is required." else ""
    val messageError = if (hasSubmitted && message.isBlank()) "Message is required." else ""
    val studentError = if (hasSubmitted && initialNote == null && selectedStudent.isBlank()) "Student is required." else ""
    val classSectionOptions = pickerState.classSectionPickerOptions
        .distinctBy { it.id }
        .sortedWith(parentNoteClassSectionComparator())
        .map { it.label to (it.className to it.sectionName) }
    val selectedClassSection = if (selectedClass.isNotBlank() && selectedSection.isNotBlank()) "$selectedClass-$selectedSection" else ""
    var isUploadingFiles by remember { mutableStateOf(false) }
    var attachmentError by remember { mutableStateOf("") }
    var uploadSession by remember(initialNote?.backendId) {
        mutableStateOf(
            FileUploadRepository.newSession("PARENT_NOTE").copy(
                recordId = initialNote?.backendId?.takeIf { it.isNotBlank() },
                uploadedFileIds = initialNote?.attachmentFileIds.orEmpty()
            )
        )
    }
    val coroutineScope = rememberCoroutineScope()
    var attachments by remember(initialNote?.backendId) {
        mutableStateOf(initialNote?.attachmentFileIds.orEmpty().mapIndexed { index, fileId ->
            ParentNoteAttachmentUi(null, "Attachment ${index + 1}", "FILE", "Existing attachment", fileId)
        })
    }

    LaunchedEffect(initialNote?.backendId, selectedClass, selectedSection, selectedStudent, pickerState.studentPickerOptions.size) {
        val studentId = initialStudent?.studentId.orEmpty().trim().ifBlank {
            pickerState.studentPickerOptions.firstOrNull {
                (it.name == selectedStudent || it.displayLabel == selectedStudent) &&
                    it.className == selectedClass &&
                    it.sectionName == selectedSection
            }?.id.orEmpty()
        }
        if (studentId.isNotBlank() && pickerState.studentPickerOptions.any { it.id == studentId }) {
            onStudentSelected(studentId)
        }
    }
    val attachmentPicker = rememberFilePickerLauncher(
        type = PickerType.File(listOf("pdf", "doc", "docx", "jpg", "jpeg", "png")),
        mode = PickerMode.Multiple(),
        title = "Select Parent Note Attachments"
    ) { files ->
        if (files != null) coroutineScope.launch {
            isUploadingFiles = true
            attachmentError = ""
            for (file in files) {
                if (attachments.any { it.name.equals(file.name, ignoreCase = true) }) continue
                val bytes = runCatching { file.readBytes() }.getOrNull()
                if (bytes == null || bytes.isEmpty()) {
                    attachmentError = "Unable to read ${file.name}."
                    continue
                }
                val isImage = file.name.substringAfterLast('.', "").lowercase() in setOf("jpg", "jpeg", "png", "webp")
                val uploadBytes = if (isImage) resizeImage(bytes) else bytes
                val uploadFilename = if (isImage && uploadBytes !== bytes) "${file.name.substringBeforeLast('.', file.name)}.jpg" else file.name
                FileUploadRepository.upload(uploadSession, uploadFilename, parentNoteContentType(uploadFilename), uploadBytes).fold(
                    onSuccess = { uploaded ->
                        uploadSession = uploaded.first
                        attachments = attachments + ParentNoteAttachmentUi(file, file.name, file.name.substringAfterLast('.', "FILE").uppercase(), formatParentNoteAttachmentSize(file.getSize()), uploaded.second.fileId, if (isImage) uploadBytes else null)
                    },
                    onFailure = { throwable -> attachmentError = throwable.message ?: "Unable to upload ${file.name}." }
                )
            }
            isUploadingFiles = false
        }
    }

    AppDialogScaffold(
        onDismiss = onDismiss,
        size = com.schoolconnect.app.designsystem.AppDialogSize.Wide,
        maxDialogHeight = 860.dp,
        isLoading = isUploadingFiles || pickerState.pickerLoading,
        loadingTitle = if (isUploadingFiles) "Uploading attachments" else "Loading student details"
    ) {
        AppDialogHeader(
            title = if (initialNote == null) "Start Parent Conversation" else "Edit Parent Note",
            subtitle = "Send a private message to a student's parent.",
            onClose = onDismiss,
            icon = Res.drawable.message,
            colors = AppDialogDefaultColors
        )
        AppDialogValidationSummary(errors = listOf(classError, studentError, titleError, messageError), modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp))
        AppDialogErrorMessage(message = errorMessage.ifBlank { validationError }, modifier = Modifier.padding(horizontal = 28.dp, vertical = 4.dp))
        Column(
            modifier = Modifier.fillMaxWidth().weight(1f, fill = false).verticalScroll(rememberScrollState()).padding(horizontal = 28.dp),
            verticalArrangement = Arrangement.spacedBy(18.dp)
        ) {
            ParentNoteDialogSection("Student Information", Res.drawable.users)
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                AppDialogFieldColumn("Class", Modifier.weight(1f), required = true) {
                    AppSearchableDropdownField(
                        label = "",
                        value = selectedClassSection,
                        placeholder = "Select class",
                        options = classSectionOptions.map { it.first },
                        onValueChange = { value ->
                            classSectionOptions.firstOrNull { it.first == value }?.second?.let { (className, sectionName) ->
                                selectedClass = className
                                selectedSection = sectionName
                                selectedStudent = ""
                                onClassSectionSelected(pickerState.classSectionPickerOptions.firstOrNull { option -> option.className == className && option.sectionName == sectionName }?.id)
                            }
                        },
                        errorText = classError,
                        valueFontWeight = FontWeight.Normal,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
                AppDialogFieldColumn("Student", Modifier.weight(1.35f), required = true) {
                    val studentOptions = pickerState.studentPickerOptions
                    AppSearchableDropdownField(
                        label = "",
                        value = selectedStudent,
                        placeholder = "Select student",
                        options = studentOptions.map { it.displayLabel },
                        onValueChange = { value ->
                            val selectedOption = studentOptions.firstOrNull { it.displayLabel == value }
                            selectedStudent = selectedOption?.name ?: value
                            selectedOption?.id?.let(onStudentSelected)
                        },
                        errorText = studentError,
                        valueFontWeight = FontWeight.Normal,
                        modifier = Modifier.fillMaxWidth(),
                        onSearchQueryChange = { onStudentPickerSearchChanged(it) },
                        hasMore = pickerState.studentPickerHasMore,
                        isLoadingMore = pickerState.studentPickerLoadingMore,
                        onLoadMore = onStudentPickerLoadMore
                    )
                }
            }
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                val selectedOption = pickerState.studentPickerOptions.firstOrNull { it.name == selectedStudent || it.displayLabel == selectedStudent }
                val contact = pickerState.selectedStudentContact?.takeIf { it.studentId == selectedOption?.id }
                AppDialogFieldColumn("Roll No.", Modifier.weight(1f)) { AppDialogTextField(selectedOption?.rollNumber.orEmpty(), {}, "Roll number", colors = AppDialogDefaultColors, readOnly = true) }
                AppDialogFieldColumn("Parent Name", Modifier.weight(1f)) { AppDialogTextField(contact?.parentName.orEmpty(), {}, "Parent name", colors = AppDialogDefaultColors, readOnly = true) }
                AppDialogFieldColumn("Mobile Number", Modifier.weight(1.35f)) { AppDialogTextField(contact?.mobile.orEmpty(), {}, "Mobile number", colors = AppDialogDefaultColors, readOnly = true, trailingIcon = { Icon(painterResource(Res.drawable.phone), "Call parent", tint = AppPrimary, modifier = Modifier.size(22.dp)) }) }
            }

            ParentNoteDialogSection("Conversation Details", Res.drawable.message)
            AppDialogFieldColumn("Title", required = true, error = titleError) {
                AppDialogTextField(title, { title = it }, "Enter note title", colors = AppDialogDefaultColors, maxLength = 160, error = titleError)
            }
            AppDialogFieldColumn("Message", required = true, error = messageError) {
                AppDialogTextField(
                    value = message,
                    onValueChange = { message = it },
                    placeholder = "Write your message",
                    colors = AppDialogDefaultColors,
                    singleLine = false,
                    fieldHeight = 170.dp,
                    fontSize = 14.sp,
                    maxLength = 2000
                    ,error = messageError
                )
            }

            AppDialogFieldColumn(label = "Attachments", helperText = "Optional", colors = AppDialogDefaultColors) {
                Row(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    AppOutlinedButton(onClick = { attachmentPicker.launch() }, modifier = Modifier.width(170.dp).height(44.dp), contentPadding = PaddingValues(horizontal = 12.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(8.dp)) {
                        Icon(painterResource(Res.drawable.cloud_upload), null, tint = AppPrimary, modifier = Modifier.size(18.dp)); Spacer(Modifier.width(8.dp)); Text("Upload Files", color = AppPrimary, fontWeight = FontWeight.SemiBold)
                    }
                    Spacer(Modifier.width(14.dp))
                    Text(if (attachments.isEmpty()) "No files selected" else "${attachments.size} file${if (attachments.size == 1) "" else "s"} selected", color = AppMuted, fontSize = 13.sp)
                }
                if (attachments.isNotEmpty()) {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        attachments.chunked(2).forEach { row ->
                            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                                row.forEach { attachment ->
                                ParentNoteAttachmentChip(attachment, Modifier.weight(1f)) {
                                        val fileId = attachment.fileId
                                            ?.takeIf { it !in initialNote?.attachmentFileIds.orEmpty() }
                                        if (fileId == null) {
                                            // Existing files are detached when the edit is saved; deleting them here
                                            // would make Cancel destructive.
                                            attachments = attachments - attachment
                                        } else {
                                            coroutineScope.launch {
                                                FileUploadRepository.delete(fileId).fold(
                                                    onSuccess = { attachments = attachments - attachment },
                                                    onFailure = { throwable -> attachmentError = throwable.message ?: "Unable to delete ${attachment.name}." }
                                                )
                                            }
                                        }
                                    }
                                }
                                if (row.size == 1) Spacer(Modifier.weight(1f))
                            }
                        }
                    }
                }
                Text("Supported: PDF, DOC, DOCX, JPG, PNG (Max 10 MB per file)", color = AppMuted, fontSize = 12.sp)
                if (attachmentError.isNotBlank()) Text(attachmentError, color = com.schoolconnect.app.designsystem.AppDanger, fontSize = 12.sp)
            }
        }
        com.schoolconnect.app.designsystem.AppDialogDivider()
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Cancel", onDismiss, AppDialogActionStyle.Secondary, enabled = !isUploadingFiles),
                AppDialogFooterAction("Save & Send", {
                    hasSubmitted = true
                    val selectedOption = pickerState.studentPickerOptions.firstOrNull {
                        (it.name == selectedStudent || it.displayLabel == selectedStudent) &&
                            it.className == selectedClass &&
                            it.sectionName == selectedSection
                    }
                    val contact = pickerState.selectedStudentContact?.takeIf { it.studentId == selectedOption?.id }
                    val missingRequiredValue = selectedClass.isBlank() ||
                        selectedSection.isBlank() ||
                        title.isBlank() ||
                        message.isBlank() ||
                        (initialNote == null && (selectedStudent.isBlank() || selectedOption == null || contact?.parentId.isNullOrBlank()))
                    if (missingRequiredValue) {
                        validationError = "Select a student and complete all required fields"
                    } else if (initialNote != null) {
                        validationError = ""
                        onUpdate(ParentNoteEditDraft(initialNote.backendId, title, message, attachments.mapNotNull { it.fileId }))
                        onDismiss()
                    } else {
                        selectedOption?.let { option ->
                            contact?.parentId?.takeIf { it.isNotBlank() }?.let { parentId ->
                                validationError = ""
                                onSave(ParentNoteDraft(option.id, parentId, title, message, attachments.mapNotNull { it.fileId }))
                                onDismiss()
                            }
                        }
                    }
                }, AppDialogActionStyle.Primary, enabled = !isUploadingFiles)
            ),
            colors = AppDialogDefaultColors
        )
    }
}

@Composable
private fun ParentNoteAttachmentChip(attachment: ParentNoteAttachmentUi, modifier: Modifier, onRemove: () -> Unit) {
    Row(
        modifier = modifier.height(72.dp).border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(horizontal = 10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        val preview = attachment.previewBytes?.let(::decodeImageBitmap)
        if (preview != null) {
            Image(bitmap = preview, contentDescription = attachment.name, modifier = Modifier.size(52.dp), contentScale = ContentScale.Fit)
        } else {
            Box(Modifier.size(36.dp).background(if (attachment.type == "PDF") Color(0xFFFFECEC) else Color(0xFFEAF8EF), RoundedCornerShape(6.dp)), contentAlignment = Alignment.Center) {
                Icon(painterResource(Res.drawable.document), null, tint = AppPrimary, modifier = Modifier.size(19.dp))
            }
        }
        Spacer(Modifier.width(8.dp))
        Column(Modifier.weight(1f)) {
            Text(attachment.name, color = AppNavy, fontSize = 12.sp, maxLines = 1)
            Text("${attachment.type}  •  ${attachment.sizeLabel}", color = AppMuted, fontSize = 11.sp)
        }
        Icon(painterResource(Res.drawable.close_x), "Remove ${attachment.name}", tint = AppNavy, modifier = Modifier.size(18.dp).clickable(onClick = onRemove))
    }
}

private fun parentNoteContentType(filename: String): String = when (filename.substringAfterLast('.', "").lowercase()) {
    "pdf" -> "application/pdf"
    "doc" -> "application/msword"
    "docx" -> "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    "jpg", "jpeg" -> "image/jpeg"
    "png" -> "image/png"
    else -> "application/octet-stream"
}

private fun formatParentNoteAttachmentSize(bytes: Long?): String {
    if (bytes == null || bytes <= 0L) return "Size unavailable"
    return if (bytes >= 1024L * 1024L) "${bytes / (1024L * 1024L)} MB" else "${(bytes / 1024L).coerceAtLeast(1L)} KB"
}

@Composable
private fun ParentNoteDialogSection(title: String, icon: org.jetbrains.compose.resources.DrawableResource) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
        Icon(painterResource(icon), null, tint = AppPrimary, modifier = Modifier.size(24.dp))
        AppDialogSectionLabel(title, colors = AppDialogDefaultColors, fontSize = 19.sp)
    }
}
